import React, {FC, useEffect, useMemo, useState} from 'react'
import {Button} from 'react-bootstrap'
import { CATEGORY_ATTRIBUTE_FOR_SEARCH, CATEGORY_ITEM_ON_UPDATE } from 'src/interfaces/Template.interface'
import {KTIcon} from 'src/marketConnect/helpers'
import {getPlatformFieldsAsync} from 'src/redux/actions/productActions'
import {
  deleteCategoryTemplateDataAsync,
  getPlatformCategoryTemplateDataAsync,
} from 'src/redux/actions/templateAndPricingActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import Loader from 'src/utils/Loader'
import Select, { GroupBase } from 'react-select'
import clsx from 'clsx'
import InfoTooltip from '../../listing/components/InfoTooltip'
import CategoryAttributeForSelect from './CategoryAttributeForSelect'
import CategoryAttributeForMultiSelect from './CategoryAttributeForMultiSelect'
import SelectAttributeUnit from './SelectAttributeUnit'
import ListingProfileFieldSearchSelect from './ListingProfileFieldSearchSelect'
import {
  getCategoryAttributeFieldId,
  indexLatestSavedAttributesByField,
  isCategoryAttributeDefinitionRequired,
} from './utils/categoryAttributeIdentity'
import CategoryAttributeFieldLabel from './CategoryAttributeFieldLabel'

interface Attribute {
  id: number
  api_name: string
  api_id: string
  api_code: string
  updated_at: string
  created_at: string
  platform_category_attribute: number
}

interface Category {
  id: number
  api_name: string
  api_id: string
  api_code: string
  updated_at: string
  created_at: string
  parent_data: number
  platform: number
  platform_field: number
}

type CategoryAttribute = {
  id: number
  category: Category
  api_id: string
  api_code: string
  api_name: string
  display_name: string
  isRequired: boolean
  is_required?: boolean
  addtional_information?: string | null
  attribute_type?: string | null
  input_type: string
  description: string
  hasUnits:boolean
}

interface AttributeInputProps {
  categoryAttributes: CategoryAttribute[]
  paltformId: any
  handleAttributeSelection: (
    data: {
      attribute_field: number
      value_str?: string | null
      value?: number | null
      mapped_field?: number | null | string
    }[]
  ) => void
  updatingMapData: CATEGORY_ITEM_ON_UPDATE | null
  nativeFields?: any[]
  customFields?: any[]
  /** Listing profile create/edit: scroll cap + required-first display order only */
  layoutVariant?: 'default' | 'listingProfile'
}

const sortAttributesRequiredFirstForListingProfile = (
  attributes: CategoryAttribute[]
): CategoryAttribute[] => {
  return attributes
    .map((attr, index) => ({ attr, index }))
    .sort((a, b) => {
      const aRequired = isCategoryAttributeDefinitionRequired(a.attr)
      const bRequired = isCategoryAttributeDefinitionRequired(b.attr)
      if (aRequired !== bRequired) {
        return aRequired ? -1 : 1
      }
      return a.index - b.index
    })
    .map(({ attr }) => attr)
}

const InputAttributes: FC<AttributeInputProps> = ({
  categoryAttributes,
  paltformId,
  handleAttributeSelection,
  updatingMapData,
  nativeFields,
  customFields,
  layoutVariant = 'default',
}) => {
  const isListingProfileLayout = layoutVariant === 'listingProfile'
  const dispatch = useAppDispatch()

  const [selectedTextValues, setSelectedTextValues] = useState<{[key: number]: string | null}>({})
  const {mainStoreData, erpStoreData, omsStoreData, accountingStoreData, shippingStoreData, ticketStoreData, fetchedSourcePlatformData, fetchedConnectedPlatformData} = useAppSelector((state) => state.fetchConnectedPlatform) || {}
  const apiSource = fetchedSourcePlatformData?.source_platform
  const sourceStore = apiSource?.id
    ? apiSource
    : mainStoreData ?? omsStoreData ?? erpStoreData ?? accountingStoreData ?? shippingStoreData ?? null
  const [selectedValues, setSelectedValues] = useState<{[key: number]: number | null}>({})
  const [selectedFieldValues, setSelectedFieldValues] = useState<{[key: number]: number }>({})
  const [selectedUnitValues, setSelectedUnitValues] = useState<{[key: number]: number }>({})
  const [attributeMappingOptions, setAttributeMappingOptions] = useState<{[key: number]: string}>(
    () => {
      const initialMappingOptions: {[key: number]: string} = {}
      const safeCategoryAttributes = Array.isArray(categoryAttributes) ? categoryAttributes : [];
      safeCategoryAttributes.forEach((attribute) => {
        initialMappingOptions[attribute.id] = 'DEFAULT'
      })
      return initialMappingOptions
    }
  )

  const [fieldData, setFieldData] = useState<any[]>([])
  const [fieldDataCustom, setFieldDataCustom] = useState<any[]>([])
  const [isMappingLoading, setIsMappingLoading] = useState(true)
  const [initialValueLoading, setInitialValueLoading] = useState(true)


  useEffect(() => {
    if (isMappingLoading || initialValueLoading) return
    const safeCategoryAttributes = Array.isArray(categoryAttributes) ? categoryAttributes : []
    if (safeCategoryAttributes.length === 0) return
    constructPayloadAttribute(
      attributeMappingOptions,
      selectedTextValues,
      selectedValues,
      selectedFieldValues,
      selectedUnitValues
    )
  }, [
    attributeMappingOptions,
    selectedTextValues,
    selectedValues,
    selectedFieldValues,
    selectedUnitValues,
    isMappingLoading,
    initialValueLoading,
    categoryAttributes,
  ])

  useEffect(() => {
    if (paltformId) {
      if (!sourceStore?.id) {
        setInitialValueLoading(false)
        return
      }

      // If props are provided, use them and skip dispatch
      if (nativeFields && customFields) {
        setFieldData(nativeFields);
        setFieldDataCustom(customFields);
        setInitialValueLoading(false);
        return;
      }

      setInitialValueLoading(true)
      Promise.all([
        dispatch(
          getPlatformFieldsAsync({
            platformId: sourceStore.id,
            otherParams: {field_type: 'NATIVE', includeLocalFields: true, owner: 'PRODUCT'},
          })
        ).then((response) => {
          setFieldData(response.payload || [])
        }),
        dispatch(
          getPlatformFieldsAsync({
            platformId: sourceStore.id,
            otherParams: {field_type: 'CUSTOM', owner: 'PRODUCT', includeLocalFields: false},
          })
        ).then((response) => {
          setFieldDataCustom(response.payload || [])
        }),
      ]).finally(() => setInitialValueLoading(false))
    }
  }, [paltformId, sourceStore?.id, nativeFields, customFields])

  const constructPayloadAttribute = (
    mappingOptions: {[key: number]: string},
    selectedTextValues: {[key: number]: string | null},
    selectedValues: {[key: number]: number | null},
    selectedFieldValues: {[key: number]: number | null},
    selectedUnitValues: {[key: number]: number | null},

  ) => {
    const safeCategoryAttributes = Array.isArray(categoryAttributes) ? categoryAttributes : [];
    const savedByField = indexLatestSavedAttributesByField(updatingMapData?.attributes)
    const attributeData = safeCategoryAttributes.map((attribute) => {
      const mappingOption = mappingOptions[attribute.id] || 'DEFAULT' // Get the mapping option for the attribute
      const savedRowId = savedByField.get(attribute.id)?.id ?? null
      if (mappingOption === 'DEFAULT') {
        return {
          id: savedRowId,
          attribute_field: attribute.id,
          value_str: selectedTextValues[attribute.id] || null,
          value: selectedValues[attribute.id] || null,
          mapped_field: null,
          unit:selectedUnitValues[attribute.id] || null
        }
      } else {
        return {
          id: savedRowId,
          attribute_field: attribute.id,
          value_str: null,
          value: null,
          mapped_field: selectedFieldValues[attribute.id] || null,
          unit:selectedUnitValues[attribute.id] || null
        }
      }
    })
    console.log("attributeData",attributeData)
    handleAttributeSelection(attributeData)
  }

  


  const handleMappingOptionChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    attributeId: number
  ) => {
    const option = e.target.value;
    setAttributeMappingOptions((prevOptions) => ({
      ...prevOptions,
      [attributeId]: option,
    }));
  
    // Reset selected field value for the current attribute
    // setSelectedFieldValues((prevValues) => ({
    //   ...prevValues,
   
    // }));
  
    // Fetch field data based on the selected mapping option
    const fieldType = option === 'CUSTOM' ? 'CUSTOM' : 'NATIVE';
    const isDefaultionOption = option === 'DEFAULT';
    if(isDefaultionOption){
      return; // no need to fetch field data for default option
    }

    // Skip dispatch if props are available (since we already have both sets of data)
    if (nativeFields && customFields) {
      return;
    }

    if (!sourceStore?.id) {
      return
    }
    dispatch(
      getPlatformFieldsAsync({
        platformId: sourceStore.id,
        otherParams: { field_type: fieldType, includeLocalFields: option === 'NATIVE', owner: 'PRODUCT' },
      })
    ).then((response) => {

      if(fieldType==='NATIVE'){
        setFieldData(response.payload);
      }
      else{
        setFieldDataCustom(response.payload);
      }
      
    });
  };
  

  const handleValueChange = (attributeId: number, value: number | null) => {
    setSelectedValues((prevValues) => ({
      ...prevValues,
      [attributeId]: value,
    }))
     setSelectedFieldValues((prevValues) => ({
      ...prevValues,
      [attributeId]: Number(value),
    }))
    // Call constructPayloadAttribute when attribute value changes
    // constructPayloadAttribute(mappingOptions, selectedTextValues, selectedValues, selectedFieldValues);
  }
  // Handle attribute  text value change
  const handleValueTextChange = (attributeId: number, value: string) => {
    setSelectedTextValues((prevValues) => ({
      ...prevValues,
      [attributeId]: value,
    }))
    setSelectedFieldValues((prevValues) => ({
      ...prevValues,
      [attributeId]: Number(value),
    }))
    // Call constructPayloadAttribute when attribute value changes
    // constructPayloadAttribute(mappingOptions, selectedTextValues, selectedValues, selectedFieldValues);
  }

  const handleFieldValueChange = (attributeId: number, value: number ) => {
    console.log(attributeId,"setSelectedFieldValues  value--",value)
    setSelectedFieldValues((prevValues) => ({
      ...prevValues,
      [attributeId]: value,
    }))
    // Call constructPayloadAttribute when field value changes
    //   constructPayloadAttribute(mappingOptions, selectedTextValues, selectedValues, selectedFieldValues);
  }


  const handleUnitValueChange = (attributeId: number, value: number ) => {
    setSelectedUnitValues((prevValues) => ({
      ...prevValues,
      [attributeId]: value,
    }))
  }
  
  useEffect(() => {
    // on update time mappping data save default values 
    if(updatingMapData && attributeMappingOptions ){
      setIsMappingLoading(true)
      indexLatestSavedAttributesByField(updatingMapData.attributes).forEach((attr) => {
        const attrid = getCategoryAttributeFieldId(attr.attribute_field)
        if (attrid == null) return
        if(attributeMappingOptions[attrid]=='DEFAULT' ){
          // setAttributeMappingOptions((pre)=>{
          //   return {...pre, [attr.attribute_field.id]:"CUSTOM" }
          // })
          if(attr?.value?.id){
            setSelectedValues((pre)=>{
              return {...pre, [attrid]: attr?.value?.id!  }
            })
          }
          if(attr.value_str){
            setSelectedTextValues((pre)=>{
              return {...pre, [attrid]:attr?.value_str! }
            })
          }

          if(attr?.unit?.id){
            setSelectedUnitValues((prevValues) => ({
              ...prevValues,
              [attrid]: attr?.unit?.id,
            }))
          }

          if(attr.mapped_field?.id){
            setAttributeMappingOptions((pre)=>{
              if(attr.mapped_field?.field_type=='LOCAL'){
                return {...pre, [attrid]: "NATIVE" }
              }else{
                return {...pre, [attrid]: attr.mapped_field?.field_type! }
              }
            })
            setSelectedFieldValues((pre)=>{
              return {...pre, [attrid]: attr?.mapped_field?.id!  }
            })
            
          }

        }else{
          setAttributeMappingOptions((pre)=>{
            return {...pre, [attrid]: attr.mapped_field?.field_type! }
          })
          if(attr?.value?.id){
            setSelectedValues((pre)=>{
              return {...pre, [attrid]: attr?.mapped_field?.id!  }
            })
          }
          if(attr.mapped_field?.id){
            // setAttributeMappingOptions((pre)=>{
            //   return {...pre, [attrid]: attr.mapped_field?.field_type! }
            // })
            setSelectedFieldValues((pre)=>{
              return {...pre, [attrid]: attr?.mapped_field?.id!  }
            })
            
          }
        }
      })
      setIsMappingLoading(false)
    }
    setIsMappingLoading(false)
  
  }, [updatingMapData])

  const renderNativeOrCustomFieldSelect = (attribute: CategoryAttribute) => {
    const isNative = attributeMappingOptions[attribute.id] === 'NATIVE'
    const fields = isNative ? fieldData : fieldDataCustom
    const selectedId = selectedFieldValues[attribute.id]
    const fromList = fields.find((option: { id: number; display_name?: string; api_name?: string }) =>
      Number(option.id) === Number(selectedId)
    )
    const fieldChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
      handleFieldValueChange(attribute.id, parseInt(e.target.value, 10))

    if (isListingProfileLayout) {
      return (
        <ListingProfileFieldSearchSelect
          key={`${attribute.id}-${attributeMappingOptions[attribute.id] ?? 'DEFAULT'}`}
          name={String(attribute.id)}
          platformId={sourceStore?.id}
          fieldType={isNative ? 'NATIVE' : 'CUSTOM'}
          value={selectedId}
          selectedLabel={fromList?.display_name ?? fromList?.api_name ?? null}
          placeholder="Select Field"
          onChange={fieldChange}
          className="form-select product-listing__more-mapping-control"
        />
      )
    }

    return (
      <select
        className="form-select"
        required={attribute.isRequired}
        name={attribute.id + ''}
        value={selectedFieldValues[attribute.id] + ''}
        onChange={fieldChange}
      >
        <option value="">Select Field</option>
        {fields.map((option: { id: number; display_name?: string }) => (
          <option key={option.id} value={option.id}>
            {option.display_name}
          </option>
        ))}
      </select>
    )
  }

  const mainAttributesForDisplay = useMemo(() => {
    const main =
      categoryAttributes?.filter((it) => it['attribute_type'] == null) ?? []
    if (!isListingProfileLayout) return main
    return sortAttributesRequiredFirstForListingProfile(main)
  }, [categoryAttributes, isListingProfileLayout])

  const extraAttributesForDisplay = useMemo(() => {
    const extra =
      categoryAttributes?.filter((it) => it['attribute_type'] != null) ?? []
    if (!isListingProfileLayout) return extra
    return sortAttributesRequiredFirstForListingProfile(extra)
  }, [categoryAttributes, isListingProfileLayout])

  const renderMainAttributeRow = (attribute: CategoryAttribute) => (
    <div key={attribute.id} className='mb-3 row ms-3'>
      <div
        className='col-6 col-sm-auto d-flex align-items-center gap-2'
        style={{ width: '35%' }}
      >
        {isListingProfileLayout ? (
          <CategoryAttributeFieldLabel
            displayName={attribute.display_name}
            isRequired={attribute.isRequired}
            is_required={attribute.is_required}
            variant="listingProfile"
            additionalInformation={attribute.addtional_information}
            tooltipIdSuffix={attribute.id}
          />
        ) : (
          <>
            <CategoryAttributeFieldLabel
              displayName={attribute.display_name}
              isRequired={attribute.isRequired}
              is_required={attribute.is_required}
              variant="legacy"
            />
            {attribute.addtional_information ? (
              <InfoTooltip message={attribute.addtional_information} />
            ) : null}
          </>
        )}
      </div>
      <div className='col-12 col-sm-auto' style={{ width: '15%' }}>
        <div className='row align-items-center'>
          <div className=''>
            <select
              className='form-select'
              value={attributeMappingOptions[attribute.id] || 'DEFAULT'}
              onChange={(e) => handleMappingOptionChange(e, attribute.id)}
            >
              <option value='DEFAULT'>Default</option>
              <option value='NATIVE'>Native Field</option>
              <option value='CUSTOM'>Custom Field</option>
            </select>
          </div>
        </div>
      </div>
      <div
        className='col-12 col-sm-auto mt-1 mt-sm-0 row'
        style={{ width: '50%' }}
      >
        <div className={clsx(attribute.hasUnits ? 'col-8' : 'col-12')}>
          {attributeMappingOptions[attribute.id] === 'DEFAULT' ? (
            attribute.input_type === 'SELECT' ? (
              <>
                <CategoryAttributeForSelect
                  attribute={attribute}
                  selectedValues={selectedValues}
                  handleValueChange={handleValueChange}
                />
              </>
            ) : attribute.input_type === 'MULTISELECT' ? (
              <>
                <div>
                  <CategoryAttributeForMultiSelect
                    attribute={attribute}
                    selectedValues={selectedTextValues[attribute.id]}
                    handleValueChange={handleValueTextChange}
                  />
                </div>
              </>
            ) : (
              <input
                type='text'
                required={attribute.isRequired}
                className='form-control'
                placeholder={attribute.display_name}
                value={selectedTextValues[attribute.id] ?? ''}
                onChange={(e) =>
                  handleValueTextChange(attribute.id, e.target.value)
                }
              />
            )
          ) : (
            renderNativeOrCustomFieldSelect(attribute)
          )}
        </div>
        {attribute.hasUnits ? (
          <div className='col-4'>
            <SelectAttributeUnit
              attribute={attribute}
              handleValueChange={handleUnitValueChange}
              selectedValues={selectedUnitValues}
            />
          </div>
        ) : null}
      </div>
    </div>
  )

  return (
    <div className='d-flex flex-column align-items-center'>
      {/* Render attributes with mapping options */}
      { isMappingLoading || initialValueLoading ? 
      (
        <div className='d-flex align-items-center w-100 justify-content-center' >
          <Loader/> 
        </div>
      )
      
      : (categoryAttributes.length > 0 ? <div className='mb-3 pb-1 row align-items-center w-100'>
        <div className='border border-1px rounded mt-2 pb-2 mx-auto'>
        {/* mapping for extra field */}
        {
          extraAttributesForDisplay.length ? 
          <>
          <div className='align-items-start' >
            <label className='fs-8 mb-0 form-label bg-light-primary text-primary px-2 py-1 rounded position-relative' style={{top:'-10px'}} >Extra Specification Mapping</label>
          </div> 
           <div className='mb-3 row ms-3 fs-6 fw-bold'>
           <div className='col-6 col-sm-auto' style={{width: '35%'}}>Attribute Name</div>
           <div className='col-12 col-sm-auto' style={{width: '15%'}}>Mapping Type</div>
           <div className='col-12 col-sm-auto' style={{width: '50%'}}>Attribute Value</div>
           </div>
              { extraAttributesForDisplay.map((attribute) => (
                <div
                  key={attribute.id}
                  // className='mb-3 pb-1 row align-items-center w-100 p-2 px-0 ps-3'
                  className='mb-3 row ms-3'
                >
                  
                  <div className='col-6 col-sm-auto d-flex align-items-center gap-2' style={{width: '35%'}}>
                    {isListingProfileLayout ? (
                      <CategoryAttributeFieldLabel
                        displayName={attribute.display_name}
                        isRequired={attribute.isRequired}
                        is_required={attribute.is_required}
                        variant="listingProfile"
                        additionalInformation={attribute.addtional_information}
                        tooltipIdSuffix={`extra-${attribute.id}`}
                      />
                    ) : (
                      <>
                        <CategoryAttributeFieldLabel
                          displayName={attribute.display_name}
                          isRequired={attribute.isRequired}
                          is_required={attribute.is_required}
                          variant="legacy"
                        />
                        {attribute.addtional_information ? (
                          <InfoTooltip message={attribute.addtional_information} />
                        ) : null}
                      </>
                    )}
                  </div>
                  <div className='col-12 col-sm-auto' style={{width: '15%'}} >
                      <div className='row align-items-center'>
                        <div className="">
                          <select
                            className='form-select'
                            value={attributeMappingOptions[attribute.id] || 'DEFAULT'}
                            onChange={(e) => handleMappingOptionChange(e, attribute.id)}
                          >
                            <option value='DEFAULT'>Default</option>
                            <option value='NATIVE'>Native Field</option>
                            <option value='CUSTOM'>Custom Field</option>
                          </select>
                        </div>
                      </div>
                  </div>
                  <div className='col-12 col-sm-auto mt-1 mt-sm-0 row' style={{width: '50%'}} >
                      {/* Render the mapping option dropdown */}
                      {/* Render input field based on selected mapping option */}
                      <div className={clsx(attribute.hasUnits ? 'col-8' : 'col-12' )} >
                        {attributeMappingOptions[attribute.id] === 'DEFAULT' ? (
                          // Render default input field
                          attribute.input_type === 'SELECT' ? (
                            <>
                            <CategoryAttributeForSelect
                              attribute={attribute}
                              selectedValues={selectedValues}
                              handleValueChange={handleValueChange}
                            />
                            </>
                          ) : attribute.input_type ==='MULTISELECT' ? 
                          (
                            <>
                              <div>
                                <CategoryAttributeForMultiSelect
                                  attribute={attribute}
                                  selectedValues={selectedTextValues[attribute.id]}
                                  handleValueChange={handleValueTextChange}
                                />
                              </div>
                            </>
                          ) 
                          :
                          (
                            <input
                              type='text'
                              required={attribute.isRequired}
                              className='form-control'
                              placeholder={attribute.display_name}
                              value={attributeMappingOptions[attribute.id] === 'DEFAULT'
                                ? selectedTextValues[attribute.id] || ""
                                : selectedFieldValues[attribute.id] || ""}
                              onChange={(e) => handleValueTextChange(attribute.id, e.target.value)}
                            />
                          )
                      ) : (
                        renderNativeOrCustomFieldSelect(attribute)
                        )}
                      </div>
                      {
                        attribute.hasUnits ? 
                        (
                          <div className='col-4' >
                            <SelectAttributeUnit
                              attribute={attribute}
                              handleValueChange={handleUnitValueChange}
                              selectedValues={selectedUnitValues}
          
                            />
                          </div>
                        ) : null
                      }
                    
                  </div>
                </div>
              ))
            }
          </>
          : null
        }

        <div className={clsx('mt-2 pt-2 mx-auto', isListingProfileLayout && 'w-100')}>
          {mainAttributesForDisplay.map((attribute) =>
            renderMainAttributeRow(attribute)
          )}
        </div>
        </div>
      </div> : <div className="w-100 d-flex justify-content-center align-items-center text-muted my-4">
        <div className="text-center">
          {!isListingProfileLayout ? (
            <i className="bi bi-exclamation-circle fs-4 d-block mb-2" />
          ) : null}
          <div className="fw-semibold">
            {isListingProfileLayout ? 'No attributes available' : 'No Map Attributes Available'}
          </div>
        </div>
      </div>
      )
      }
    </div>
  )
}

export default InputAttributes
